import { useEffect, useState } from "react";
import { reviewFieldText, reviewTemplate, type ReviewItem } from "../../shared/review-templates";
import type { ApprovalDTO } from "../../shared/types";
import { canCheckSend, canSettleRelease } from "../lib/approval-text";
import { reviewPagePath } from "../lib/review-paths";
import { useLocation } from "../lib/router";
import { useUser } from "../App";
import { AgentAvatar } from "./AgentAvatar";
import { SlackMessageCard } from "./SlackMessageCard";
import { TextLink } from "./TextLink";
import { Badge } from "./ui";
import { Decided, LockedVerdicts, ReleaseOutcome, VerdictBar } from "./Verdicts";

/** The item exactly as the agent filed it, field by field, in the template's own order. */
export function ReviewFields({ a }: { a: ApprovalDTO }) {
  const template = a.template ? reviewTemplate(a.template) : null;
  const item = (a.payload ?? {}) as ReviewItem;
  const rows = (template?.fields ?? []).map((f) => [f.label, reviewFieldText(f, item[f.key])] as const).filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1.5 rounded-xl bg-fill-subtle p-3 text-sm sm:grid-cols-[7rem_minmax(0,1fr)]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-xs font-medium text-muted sm:pt-0.5">{label}</dt>
          <dd className="min-w-0 break-words whitespace-pre-wrap text-muted">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * One templated request: the template's one-line summary, the item's fields as a person reads them, and the three
 * verdicts for whoever it is addressed to. The same card renders in the chat, on the Requests page and on the
 * template's own page, so a verdict given anywhere is the row's verdict. Approving one that sends is the send: the
 * card then says whether it went, and while it has not, its addressee sends it again or withdraws it.
 */
export function ReviewCard(props: {
  approval: ApprovalDTO;
  canDecide: boolean;
  /** In a chat, the card links to the page built around this kind of request; the page itself does not link to itself. */
  linkToPage?: boolean;
  onDecided?: (updated: ApprovalDTO) => void;
}) {
  // A Slack message is read as Slack will show it, wherever its card is drawn.
  if (props.approval.template === "slack-message") return <SlackMessageCard approval={props.approval} canDecide={props.canDecide} onDecided={props.onDecided} />;
  return <TemplatedCard {...props} />;
}

function TemplatedCard({ approval, canDecide, linkToPage = false, onDecided }: {
  approval: ApprovalDTO;
  canDecide: boolean;
  linkToPage?: boolean;
  onDecided?: (updated: ApprovalDTO) => void;
}) {
  // What we just decided holds until the server's own copy arrives (the websocket, or the next load).
  const [local, setLocal] = useState<ApprovalDTO | null>(null);
  useEffect(() => setLocal(null), [approval]);
  const a = local ?? approval;
  const user = useUser();
  const template = a.template ? reviewTemplate(a.template) : null;
  const keep = (updated: ApprovalDTO) => {
    setLocal(updated);
    onDecided?.(updated);
  };
  // The page opens in the space the card is read in, so an admin keeps the admin shell.
  const inAdmin = useLocation().pathname.startsWith("/admin");

  return (
    <div className="card border-l-2 border-l-amber-400/70 p-5 md:p-6">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <AgentAvatar agent={a.agentId} name={a.agentName} size="sm" />
        <Badge tone="amber">{template?.label ?? "Request"}</Badge>
        {a.version > 1 && <Badge tone="zinc">version {a.version}</Badge>}
        <span className="text-muted">
          <span className="font-medium text-fg">{a.agentName}</span> asks for a verdict before this is used
        </span>
        {linkToPage && template && (
          <TextLink to={`${reviewPagePath(template.route, inAdmin)}?item=${a.id}`} quiet className="text-xs">
            open the {template.label.toLowerCase()} page
          </TextLink>
        )}
      </div>
      <p className="mt-2 text-reading leading-6 font-medium tracking-tight break-words">{a.title}</p>
      <ReviewFields a={a} />
      {a.status !== "pending" ? (
        <>
          <Decided a={a} />
          <ReleaseOutcome a={a} canAct={canSettleRelease(a, user)} canCheck={canCheckSend(a, user)} onChanged={keep} />
        </>
      ) : canDecide ? (
        <div className="mt-4">
          <VerdictBar a={a} onDecided={(updated) => updated && keep(updated)} />
        </div>
      ) : (
        // Somebody else's to decide — an admin reading their chat or their queue reads it and leaves it alone.
        <div className="mt-4">
          <LockedVerdicts a={a} />
        </div>
      )}
    </div>
  );
}
